/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InsightsIcon from '@mui/icons-material/Insights';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'react-toastify';

import { modelLabAssetApi, modelLabVersionApi } from '../../services/api';
import { PythonCodeEditor } from '../expert/PythonCodeEditor';
import type {
  ModelLabAsset,
  ModelLabAssetCreate,
  ModelLabFamily,
  ModelLabFramework,
  ModelLabProblemType,
  ModelLineage,
} from '../../types';
import {
  ConfirmDialog,
  ErrorAlert,
  LoadingSpinner,
  PageHeader,
} from '../common';
import {
  formatMetricSummary,
  getApiErrorMessage,
  modelStageColor,
  parseCommaList,
  parseJsonInput,
} from './utils';
import { LineageGraph } from './charts';
import { TEMPLATE_FAMILIES, ALL_TEMPLATES, RECIPE_DOCS, type CodeTemplate } from './codeTemplates';

interface AssetFormState {
  name: string;
  description: string;
  family: ModelLabFamily;
  framework: ModelLabFramework;
  problemType: ModelLabProblemType;
  tags: string;
  authoringMode: string;
  executor: string;
  runtimeOverridesJson: string;
  builderTemplate: string;
  builderGoal: string;
  inputShape: string;
  featureStrategy: string;
  targetName: string;
  featureColumns: string;
  preprocessingSteps: string;
  objectiveMetric: string;
  evaluationMetrics: string;
  validationStrategy: string;
  outputSchemaJson: string;
  authoringNotes: string;
  allowedLibraries: string;
  entrypoint: string;
  recipeCode: string;
}

const defaultRecipeCode = `# Restricted model recipe
#
# Available inputs when this recipe is executed:
# - dataset_records: raw dataset rows
# - training_config: selected training configuration
# - runtime_spec: model runtime and resource settings
# - metadata: user-provided metadata
#
# Return metadata through the result variable.

feature_columns = training_config.get('feature_columns', [])
target_column = training_config.get('target_column', 'target')

result = {
    'entrypoint': 'train_model',
    'feature_columns': feature_columns,
    'target_column': target_column,
    'preprocessing': ['standardize_numeric'],
    'notes': 'Custom training recipe placeholder',
}
`;

/* ── Architecture Canvas types & catalogs ── */
const nextBlockId = () => `blk_${Math.random().toString(36).slice(2, 8)}`;

interface PreprocessingStep {
  id: string;
  type: string;
  label: string;
  params: Record<string, string>;
}

interface BaseEstimatorEntry {
  id: string;
  algorithm: string;
  params: Record<string, string>;
}

interface ArchitectureState {
  preprocessing: PreprocessingStep[];
  modelType: 'single' | 'voting' | 'stacking';
  singleEstimator: { algorithm: string; params: Record<string, string> };
  votingStrategy: 'hard' | 'soft';
  baseEstimators: BaseEstimatorEntry[];
  metaLearner: { algorithm: string; params: Record<string, string> };
}

const CANVAS_ALGORITHMS: { value: string; label: string; tasks: string[]; category: 'ml' | 'dl'; defaults: Record<string, string> }[] = [
  // ── Classical ML — Classification ──
  { value: 'logistic_regression', label: 'Logistic Regression', tasks: ['classification'], category: 'ml', defaults: { C: '1.0', max_iter: '200' } },
  { value: 'naive_bayes', label: 'Naive Bayes', tasks: ['classification'], category: 'ml', defaults: {} },
  { value: 'sgd_classifier', label: 'SGD Classifier', tasks: ['classification'], category: 'ml', defaults: { loss: 'hinge', max_iter: '1000' } },
  // ── Classical ML — Regression ──
  { value: 'linear_regression', label: 'Linear Regression', tasks: ['regression'], category: 'ml', defaults: {} },
  { value: 'ridge', label: 'Ridge', tasks: ['regression'], category: 'ml', defaults: { alpha: '1.0' } },
  { value: 'lasso', label: 'Lasso', tasks: ['regression'], category: 'ml', defaults: { alpha: '1.0' } },
  { value: 'elastic_net', label: 'Elastic Net', tasks: ['regression'], category: 'ml', defaults: { alpha: '1.0', l1_ratio: '0.5' } },
  { value: 'sgd_regressor', label: 'SGD Regressor', tasks: ['regression'], category: 'ml', defaults: { loss: 'squared_error', max_iter: '1000' } },
  // ── Classical ML — Both ──
  { value: 'random_forest', label: 'Random Forest', tasks: ['classification', 'regression'], category: 'ml', defaults: { n_estimators: '100', max_depth: '10' } },
  { value: 'gradient_boosting', label: 'Gradient Boosting', tasks: ['classification', 'regression'], category: 'ml', defaults: { n_estimators: '100', learning_rate: '0.1', max_depth: '3' } },
  { value: 'extra_trees', label: 'Extra Trees', tasks: ['classification', 'regression'], category: 'ml', defaults: { n_estimators: '100' } },
  { value: 'adaboost', label: 'AdaBoost', tasks: ['classification', 'regression'], category: 'ml', defaults: { n_estimators: '50', learning_rate: '1.0' } },
  { value: 'decision_tree', label: 'Decision Tree', tasks: ['classification', 'regression'], category: 'ml', defaults: { max_depth: '10' } },
  { value: 'knn', label: 'K-Nearest Neighbors', tasks: ['classification', 'regression'], category: 'ml', defaults: { n_neighbors: '5' } },
  { value: 'svm', label: 'SVM', tasks: ['classification', 'regression'], category: 'ml', defaults: { C: '1.0', kernel: 'rbf' } },
  { value: 'mlp', label: 'Neural Network (MLP)', tasks: ['classification', 'regression'], category: 'ml', defaults: { hidden_layer_sizes: '100,50', max_iter: '300' } },
  { value: 'xgboost', label: 'XGBoost', tasks: ['classification', 'regression'], category: 'ml', defaults: { n_estimators: '100', learning_rate: '0.1', max_depth: '6' } },
  { value: 'lightgbm', label: 'LightGBM', tasks: ['classification', 'regression'], category: 'ml', defaults: { n_estimators: '100', learning_rate: '0.1', num_leaves: '31' } },
  // ── Deep Learning — Recurrent ──
  { value: 'lstm', label: 'LSTM', tasks: ['classification', 'regression', 'time_series'], category: 'dl', defaults: { hidden_size: '64', num_layers: '2', dropout: '0.2', bidirectional: 'false' } },
  { value: 'bilstm', label: 'Bidirectional LSTM', tasks: ['classification', 'regression', 'time_series'], category: 'dl', defaults: { hidden_size: '64', num_layers: '2', dropout: '0.2' } },
  { value: 'gru', label: 'GRU', tasks: ['classification', 'regression', 'time_series'], category: 'dl', defaults: { hidden_size: '64', num_layers: '2', dropout: '0.2' } },
  { value: 'xlstm', label: 'xLSTM', tasks: ['classification', 'regression', 'time_series'], category: 'dl', defaults: { hidden_size: '128', num_layers: '3', dropout: '0.1', block_type: 'slstm' } },
  // ── Deep Learning — Transformer ──
  { value: 'transformer', label: 'Transformer Encoder', tasks: ['classification', 'regression', 'time_series'], category: 'dl', defaults: { d_model: '64', nhead: '4', num_layers: '2', dim_feedforward: '256', dropout: '0.1' } },
  { value: 'temporal_fusion_transformer', label: 'Temporal Fusion Transformer', tasks: ['regression', 'time_series'], category: 'dl', defaults: { hidden_size: '64', num_heads: '4', num_layers: '2', dropout: '0.1' } },
  { value: 'informer', label: 'Informer', tasks: ['regression', 'time_series'], category: 'dl', defaults: { d_model: '512', nhead: '8', num_encoder_layers: '2', num_decoder_layers: '1', dropout: '0.05' } },
  { value: 'patch_tst', label: 'PatchTST', tasks: ['regression', 'time_series'], category: 'dl', defaults: { d_model: '128', nhead: '4', num_layers: '3', patch_length: '16', stride: '8' } },
  // ── Deep Learning — Convolutional ──
  { value: 'cnn_1d', label: '1D CNN', tasks: ['classification', 'regression', 'time_series'], category: 'dl', defaults: { filters: '64', kernel_size: '3', num_conv_layers: '3', dropout: '0.2' } },
  { value: 'tcn', label: 'Temporal Conv Network (TCN)', tasks: ['classification', 'regression', 'time_series'], category: 'dl', defaults: { num_channels: '64', kernel_size: '3', num_layers: '4', dropout: '0.2' } },
  { value: 'conv_lstm', label: 'ConvLSTM', tasks: ['time_series'], category: 'dl', defaults: { filters: '64', kernel_size: '3', lstm_units: '64', dropout: '0.2' } },
  // ── Deep Learning — Advanced ──
  { value: 'wavenet', label: 'WaveNet', tasks: ['regression', 'time_series'], category: 'dl', defaults: { residual_channels: '32', skip_channels: '32', num_layers: '8', dropout: '0.1' } },
  { value: 'nbeats', label: 'N-BEATS', tasks: ['regression', 'time_series'], category: 'dl', defaults: { num_stacks: '2', num_blocks: '3', num_layers: '4', layer_width: '256' } },
  { value: 'autoencoder', label: 'Autoencoder', tasks: ['classification', 'regression'], category: 'dl', defaults: { encoder_layers: '128,64,32', decoder_layers: '64,128', dropout: '0.2' } },
  { value: 'vae', label: 'Variational Autoencoder', tasks: ['classification', 'regression'], category: 'dl', defaults: { encoder_layers: '128,64', latent_dim: '16', decoder_layers: '64,128', dropout: '0.2' } },
  { value: 'attention_network', label: 'Multi-Head Attention Net', tasks: ['classification', 'regression', 'time_series'], category: 'dl', defaults: { hidden_size: '64', num_heads: '4', dropout: '0.1' } },
];

/** Derive the asset's family & framework from the chosen algorithm */
const DL_ALGORITHM_SET = new Set(
  CANVAS_ALGORITHMS.filter((a) => a.category === 'dl').map((a) => a.value),
);
const deriveFamilyFramework = (algorithm: string): { family: 'ML' | 'DL'; framework: 'SKLEARN' | 'PYTORCH' } =>
  DL_ALGORITHM_SET.has(algorithm)
    ? { family: 'DL', framework: 'PYTORCH' }
    : { family: 'ML', framework: 'SKLEARN' };

const PREPROCESSING_CATALOG: { value: string; label: string; cat: string; desc: string; family: 'all' | 'ml' | 'dl'; params: Record<string, string> }[] = [
  // ── Scaling (all families) ──
  { value: 'standard_scaler', label: 'Standard Scaler', cat: 'Scaling', desc: 'Zero mean, unit variance', family: 'all', params: {} },
  { value: 'minmax_scaler', label: 'MinMax Scaler', cat: 'Scaling', desc: 'Scale to [0, 1]', family: 'all', params: {} },
  { value: 'robust_scaler', label: 'Robust Scaler', cat: 'Scaling', desc: 'Median & IQR based', family: 'all', params: {} },
  { value: 'maxabs_scaler', label: 'MaxAbs Scaler', cat: 'Scaling', desc: 'Scale by max absolute', family: 'all', params: {} },
  // ── Missing Data (all families) ──
  { value: 'imputer_mean', label: 'Imputer (Mean)', cat: 'Missing Data', desc: 'Fill NaN with mean', family: 'all', params: {} },
  { value: 'imputer_median', label: 'Imputer (Median)', cat: 'Missing Data', desc: 'Fill NaN with median', family: 'all', params: {} },
  { value: 'imputer_mode', label: 'Imputer (Mode)', cat: 'Missing Data', desc: 'Fill NaN with most frequent', family: 'all', params: {} },
  // ── Feature Engineering (ML) ──
  { value: 'pca', label: 'PCA', cat: 'Feature Engineering', desc: 'Dimensionality reduction', family: 'ml', params: { n_components: '10' } },
  { value: 'select_k_best', label: 'SelectKBest', cat: 'Feature Engineering', desc: 'Top-K feature selection', family: 'ml', params: { k: '10' } },
  { value: 'variance_threshold', label: 'Variance Threshold', cat: 'Feature Engineering', desc: 'Remove low-variance features', family: 'ml', params: { threshold: '0.0' } },
  // ── DL Data Preparation ──
  { value: 'sequence_windowing', label: 'Sequence Windowing', cat: 'DL Prep', desc: 'Sliding windows for sequential data', family: 'dl', params: { window_size: '30', stride: '1' } },
  { value: 'log_transform', label: 'Log Transform', cat: 'DL Prep', desc: 'Stabilize variance with log(1+x)', family: 'dl', params: {} },
  { value: 'differencing', label: 'Differencing', cat: 'DL Prep', desc: 'First-order differencing for stationarity', family: 'dl', params: { periods: '1' } },
  { value: 'target_encoding', label: 'Target Encoding', cat: 'DL Prep', desc: 'Encode categoricals by target mean', family: 'dl', params: {} },
];

const BLOCK_COLORS: Record<string, string> = {
  input: '#1976d2',
  preprocessing: '#ed6c02',
  estimator: '#2e7d32',
  ensemble: '#7b1fa2',
  meta_learner: '#c2185b',
  output: '#0288d1',
};

const initialArchitecture: ArchitectureState = {
  preprocessing: [],
  modelType: 'single',
  singleEstimator: { algorithm: 'random_forest', params: { n_estimators: '100', max_depth: '10' } },
  votingStrategy: 'hard',
  baseEstimators: [
    { id: 'init_1', algorithm: 'random_forest', params: { n_estimators: '100' } },
    { id: 'init_2', algorithm: 'gradient_boosting', params: { n_estimators: '100' } },
  ],
  metaLearner: { algorithm: 'logistic_regression', params: { C: '1.0' } },
};

/* ── Canvas visual sub-components ── */
const FlowConnector: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.25 }}>
    <Box
      sx={{
        width: 2,
        height: 28,
        bgcolor: 'divider',
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -4,
          left: -3,
          borderLeft: '4px solid transparent',
          borderRight: '4px solid transparent',
          borderTop: '6px solid',
          borderTopColor: 'divider',
        },
      }}
    />
  </Box>
);

const CanvasBlock: React.FC<{
  color: string;
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
  onRemove?: () => void;
}> = ({ color, title, subtitle, children, onRemove }) => (
  <Paper
    variant="outlined"
    sx={{
      borderLeft: `4px solid ${color}`,
      p: 1.5,
      transition: 'box-shadow 0.15s',
      '&:hover': { boxShadow: 3 },
    }}
  >
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ color, fontWeight: 700, fontSize: 13 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {children && <Box sx={{ mt: 1 }}>{children}</Box>}
      </Box>
      {onRemove && (
        <IconButton size="small" onClick={onRemove} sx={{ mt: -0.5, mr: -0.5 }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Stack>
  </Paper>
);

const EstimatorConfig: React.FC<{
  algorithm: string;
  params: Record<string, string>;
  problemType: string;
  family?: string;
  onAlgorithmChange: (alg: string) => void;
  onParamChange: (key: string, value: string) => void;
  compact?: boolean;
}> = ({ algorithm, params, problemType, family, onAlgorithmChange, onParamChange, compact }) => {
  const taskFilters = problemType === 'TIME_SERIES'
    ? ['regression', 'time_series']
    : problemType === 'REGRESSION'
      ? ['regression']
      : ['classification'];
  const categoryFilter = family === 'DL' ? 'dl' : 'ml';
  const available = CANVAS_ALGORITHMS.filter(
    (a) => a.tasks.some((t) => taskFilters.includes(t)) && a.category === categoryFilter
  );
  return (
    <Stack spacing={1}>
      <TextField
        select
        label="Algorithm"
        value={algorithm}
        onChange={(e) => onAlgorithmChange(e.target.value)}
        size="small"
        fullWidth
      >
        {available.map((a) => (
          <MenuItem key={a.value} value={a.value}>
            {a.label}
          </MenuItem>
        ))}
      </TextField>
      {Object.entries(params).length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {Object.entries(params).map(([k, v]) => (
            <TextField
              key={k}
              label={k}
              value={v}
              onChange={(e) => onParamChange(k, e.target.value)}
              size="small"
              sx={{ width: compact ? 100 : 130 }}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const initialAssetForm: AssetFormState = {
  name: '',
  description: '',
  family: 'ML',
  framework: 'SKLEARN',
  problemType: 'CLASSIFICATION',
  tags: '',
  authoringMode: 'no_code',
  executor: 'cpu',
  runtimeOverridesJson: '{}',
  builderTemplate: 'tabular_classifier',
  builderGoal: 'Predict the target column from a curated feature set.',
  inputShape: 'tabular',
  featureStrategy: 'infer_from_dataset',
  targetName: 'target',
  featureColumns: '',
  preprocessingSteps: 'standardize_numeric, impute_missing',
  objectiveMetric: 'accuracy',
  evaluationMetrics: 'accuracy, f1_weighted, recall_weighted',
  validationStrategy: 'holdout',
  outputSchemaJson: '{\n  "prediction": "number",\n  "confidence": "number"\n}',
  authoringNotes: '',
  allowedLibraries: 'json, math, datetime, Decimal, numpy, pandas',
  entrypoint: 'train_model',
  recipeCode: defaultRecipeCode,
};

function getAuthoringModeLabel(authoringMode?: string): string {
  return authoringMode === 'python_sandbox'
    ? 'Restricted Python'
    : 'No-Code Builder';
}

function getRuntimeSpec(asset: ModelLabAsset): Record<string, any> {
  return asset.runtime_spec || {};
}

function getAuthoringSummary(asset: ModelLabAsset): string {
  const runtimeSpec = getRuntimeSpec(asset);
  const authoringMode = runtimeSpec.authoring_mode;
  const authoring = runtimeSpec.authoring || {};

  if (authoringMode === 'python_sandbox') {
    const pythonRecipe = authoring.python || {};
    const lineCount = String(pythonRecipe.code || '')
      .split('\n')
      .filter((line) => line.trim()).length;
    const libraries = (pythonRecipe.allowed_libraries || []).join(', ');
    return libraries
      ? `${lineCount || 0} lines • ${libraries}`
      : `${lineCount || 0} lines • default allowlist`;
  }

  const noCode = authoring.no_code || {};
  const metrics = (noCode.evaluation_metrics || []).slice(0, 2).join(', ');
  return [noCode.builder_template, noCode.objective_metric, metrics]
    .filter(Boolean)
    .join(' • ') || 'Guided builder blueprint';
}

/** Extract form + architecture state from an existing asset for edit / clone. */
function populateFromAsset(
  asset: ModelLabAsset
): { form: AssetFormState; arch: ArchitectureState } {
  const rs = asset.runtime_spec || ({} as any);
  const authoring = rs.authoring || {};
  const noCode = authoring.no_code || {};
  const python = authoring.python || {};
  const archData = noCode.architecture || {};

  const form: AssetFormState = {
    name: asset.name,
    description: asset.description || '',
    family: asset.family,
    framework: asset.framework,
    problemType: asset.problem_type,
    tags: (asset.tags || []).join(', '),
    authoringMode: rs.authoring_mode || authoring.mode || 'no_code',
    executor: rs.executor || 'cpu',
    runtimeOverridesJson: '{}',
    builderTemplate: noCode.builder_template || 'tabular_classifier',
    builderGoal: noCode.builder_goal || '',
    inputShape: noCode.input_shape || 'tabular',
    featureStrategy: noCode.feature_strategy || 'infer_from_dataset',
    targetName: noCode.target_name || 'target',
    featureColumns: (noCode.feature_columns || []).join(', '),
    preprocessingSteps: (noCode.preprocessing_steps || []).join(', '),
    objectiveMetric: noCode.objective_metric || 'accuracy',
    evaluationMetrics: (noCode.evaluation_metrics || []).join(', '),
    validationStrategy: noCode.validation_strategy || 'holdout',
    outputSchemaJson: JSON.stringify(noCode.output_schema || {}, null, 2),
    authoringNotes: noCode.notes || '',
    allowedLibraries: (python.allowed_libraries || []).join(', '),
    entrypoint: python.entrypoint || 'train_model',
    recipeCode: python.code || defaultRecipeCode,
  };

  let blockId = 1000;
  const arch: ArchitectureState = {
    preprocessing: (archData.preprocessing || []).map((s: any) => ({
      id: String(blockId++),
      type: s.type,
      label: s.label || s.type,
      params: s.params || {},
    })),
    modelType: archData.model_type || 'single',
    singleEstimator: archData.single_estimator
      ? { algorithm: archData.single_estimator.algorithm, params: { ...archData.single_estimator.params } }
      : { ...initialArchitecture.singleEstimator },
    votingStrategy: archData.voting_strategy || 'hard',
    baseEstimators: (archData.base_estimators || initialArchitecture.baseEstimators).map(
      (e: any) => ({ id: String(blockId++), algorithm: e.algorithm, params: { ...e.params } })
    ),
    metaLearner: archData.meta_learner
      ? { algorithm: archData.meta_learner.algorithm, params: { ...archData.meta_learner.params } }
      : { ...initialArchitecture.metaLearner },
  };

  return { form, arch };
}

const ModelLabRegistryPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingAssetId, setEditingAssetId] = useState<number | null>(null);
  const [assetToDelete, setAssetToDelete] = useState<ModelLabAsset | null>(null);
  const [assetToInspect, setAssetToInspect] = useState<ModelLabAsset | null>(null);
  const [assetForm, setAssetForm] = useState<AssetFormState>(initialAssetForm);
  const [lineageVersionId, setLineageVersionId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [architecture, setArchitecture] = useState<ArchitectureState>({...initialArchitecture});
  const [codeTab, setCodeTab] = useState<'template' | 'editor' | 'docs'>('template');

  const dialogMode = editingAssetId ? 'edit' : 'create';

  const applyTemplate = (t: CodeTemplate) => {
    setAssetForm((f) => ({
      ...f,
      family: t.family as ModelLabFamily,
      framework: t.framework as ModelLabFramework,
      problemType: t.problemType as ModelLabProblemType,
      allowedLibraries: t.libraries,
      entrypoint: t.entrypoint,
      recipeCode: t.code,
    }));
    setCodeTab('editor');
  };

  const openCreateDialog = (authoringMode: string) => {
    setEditingAssetId(null);
    setAssetForm((current) => ({
      ...initialAssetForm,
      family: current.family,
      framework:
        authoringMode === 'python_sandbox' ? 'CUSTOM' : initialAssetForm.framework,
      problemType: current.problemType,
      authoringMode,
    }));
    setArchitecture({...initialArchitecture});
    setCreateOpen(true);
  };

  const openEditDialog = (asset: ModelLabAsset) => {
    const { form, arch } = populateFromAsset(asset);
    setEditingAssetId(asset.id);
    setAssetForm(form);
    setArchitecture(arch);
    setCreateOpen(true);
  };

  const openCloneDialog = (asset: ModelLabAsset) => {
    const { form, arch } = populateFromAsset(asset);
    setEditingAssetId(null);
    setAssetForm({ ...form, name: `${form.name}-copy` });
    setArchitecture(arch);
    setCreateOpen(true);
  };

  const assetsQuery = useQuery({
    queryKey: ['model-lab-assets', search],
    queryFn: () => modelLabAssetApi.list({ page_size: 100, search: search || undefined }),
  });

  const versionsQuery = useQuery({
    queryKey: ['model-lab-versions'],
    queryFn: () => modelLabVersionApi.list({ page_size: 100 }),
  });

  const lineageQuery = useQuery({
    queryKey: ['model-lab-lineage', lineageVersionId],
    queryFn: () => modelLabVersionApi.lineage(lineageVersionId!),
    enabled: lineageVersionId != null,
  });

  const handleDownloadArtifact = async (versionId: number) => {
    try {
      const blob = await modelLabVersionApi.downloadArtifact(versionId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `model_version_${versionId}.pkl`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Artifact download started.');
    } catch {
      toast.error('Failed to download artifact.');
    }
  };

  const createAssetMutation = useMutation({
    mutationFn: async (form: AssetFormState) => {
      const runtimeOverrides = parseJsonInput<Record<string, any>>(
        form.runtimeOverridesJson,
        {}
      );
      const noCodeAuthoring = {
        builder_template: form.builderTemplate,
        builder_goal: form.builderGoal,
        input_shape: form.inputShape,
        feature_strategy: form.featureStrategy,
        target_name: form.targetName,
        feature_columns: parseCommaList(form.featureColumns),
        preprocessing_steps: parseCommaList(form.preprocessingSteps),
        objective_metric: form.objectiveMetric,
        evaluation_metrics: parseCommaList(form.evaluationMetrics),
        validation_strategy: form.validationStrategy,
        output_schema: parseJsonInput(form.outputSchemaJson, {}),
        notes: form.authoringNotes,
        architecture: {
          preprocessing: architecture.preprocessing.map((s) => ({ type: s.type, label: s.label, params: s.params })),
          model_type: architecture.modelType,
          single_estimator: architecture.singleEstimator,
          voting_strategy: architecture.votingStrategy,
          base_estimators: architecture.baseEstimators.map((e) => ({ algorithm: e.algorithm, params: e.params })),
          meta_learner: architecture.metaLearner,
        },
      };
      const pythonAuthoring = {
        entrypoint: form.entrypoint,
        allowed_libraries: parseCommaList(form.allowedLibraries),
        code: form.recipeCode,
      };
      const payload: ModelLabAssetCreate = {
        name: form.name,
        description: form.description,
        family: form.family,
        framework: form.framework,
        problem_type: form.problemType,
        tags: parseCommaList(form.tags),
        runtime_spec: {
          authoring_mode: form.authoringMode,
          executor: form.executor,
          authoring: {
            mode: form.authoringMode,
            no_code: noCodeAuthoring,
            python: pythonAuthoring,
          },
          ...runtimeOverrides,
        },
      };

      return modelLabAssetApi.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-assets'] });
      setCreateOpen(false);
      setEditingAssetId(null);
      setAssetForm(initialAssetForm);
      setArchitecture({...initialArchitecture});
      toast.success('Model asset and authoring blueprint saved.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to create model asset.'));
    },
  });

  const updateAssetMutation = useMutation({
    mutationFn: async ({ id, form }: { id: number; form: AssetFormState }) => {
      const runtimeOverrides = parseJsonInput<Record<string, any>>(
        form.runtimeOverridesJson,
        {}
      );
      const noCodeAuthoring = {
        builder_template: form.builderTemplate,
        builder_goal: form.builderGoal,
        input_shape: form.inputShape,
        feature_strategy: form.featureStrategy,
        target_name: form.targetName,
        feature_columns: parseCommaList(form.featureColumns),
        preprocessing_steps: parseCommaList(form.preprocessingSteps),
        objective_metric: form.objectiveMetric,
        evaluation_metrics: parseCommaList(form.evaluationMetrics),
        validation_strategy: form.validationStrategy,
        output_schema: parseJsonInput(form.outputSchemaJson, {}),
        notes: form.authoringNotes,
        architecture: {
          preprocessing: architecture.preprocessing.map((s) => ({ type: s.type, label: s.label, params: s.params })),
          model_type: architecture.modelType,
          single_estimator: architecture.singleEstimator,
          voting_strategy: architecture.votingStrategy,
          base_estimators: architecture.baseEstimators.map((e) => ({ algorithm: e.algorithm, params: e.params })),
          meta_learner: architecture.metaLearner,
        },
      };
      const pythonAuthoring = {
        entrypoint: form.entrypoint,
        allowed_libraries: parseCommaList(form.allowedLibraries),
        code: form.recipeCode,
      };
      const payload: Partial<ModelLabAssetCreate> = {
        name: form.name,
        description: form.description,
        family: form.family,
        framework: form.framework,
        problem_type: form.problemType,
        tags: parseCommaList(form.tags),
        runtime_spec: {
          authoring_mode: form.authoringMode,
          executor: form.executor,
          authoring: {
            mode: form.authoringMode,
            no_code: noCodeAuthoring,
            python: pythonAuthoring,
          },
          ...runtimeOverrides,
        },
      };
      return modelLabAssetApi.update(id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-assets'] });
      setCreateOpen(false);
      setEditingAssetId(null);
      setAssetForm(initialAssetForm);
      setArchitecture({...initialArchitecture});
      toast.success('Model asset updated.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to update model asset.'));
    },
  });

  const deleteAssetMutation = useMutation({
    mutationFn: (id: number) => modelLabAssetApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-assets'] });
      queryClient.invalidateQueries({ queryKey: ['model-lab-versions'] });
      setAssetToDelete(null);
      toast.success('Model asset deleted.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete model asset.'));
    },
  });

  const promoteMutation = useMutation({
    mutationFn: (id: number) => modelLabVersionApi.promote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-versions'] });
      toast.success('Model version promoted.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to promote model version.'));
    },
  });

  const demoteMutation = useMutation({
    mutationFn: (id: number) => modelLabVersionApi.demote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-versions'] });
      toast.success('Model version demoted.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to demote model version.'));
    },
  });

  const error = assetsQuery.error || versionsQuery.error;
  if (error) {
    return <ErrorAlert error={error as Error} />;
  }

  if (assetsQuery.isLoading || versionsQuery.isLoading) {
    return <LoadingSpinner message="Loading model registry..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Model Registry"
        breadcrumbs={[
          { label: 'Automation', to: '/ai' },
          { label: 'Model Lab', to: '/ai/ml' },
          { label: 'Model Registry' },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => openCreateDialog('no_code')}
            >
              No-Code Model
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openCreateDialog('python_sandbox')}
            >
              Code Model
            </Button>
          </Stack>
        }
      />

      <Stack spacing={2}>
        <Alert severity="info">
          This page now holds the two missing authoring modes from the Model Lab plan:
          a guided no-code builder for model blueprints and a restricted Python recipe
          editor for advanced creation flows. Runtime execution is still limited to the
          currently supported backend executors.
        </Alert>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Family</TableCell>
                <TableCell>Framework</TableCell>
                <TableCell>Problem Type</TableCell>
                <TableCell>Authoring</TableCell>
                <TableCell>Runtime</TableCell>
                <TableCell>Latest Version</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(assetsQuery.data?.results || []).map((asset) => (
                <TableRow key={asset.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{asset.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {asset.description || 'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell>{asset.family}</TableCell>
                  <TableCell>{asset.framework}</TableCell>
                  <TableCell>{asset.problem_type}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Chip
                        size="small"
                        label={getAuthoringModeLabel(asset.runtime_spec?.authoring_mode)}
                        color={
                          asset.runtime_spec?.authoring_mode === 'python_sandbox'
                            ? 'secondary'
                            : 'primary'
                        }
                        variant="outlined"
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {getAuthoringSummary(asset)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Chip
                        size="small"
                        variant="outlined"
                        label={asset.runtime_spec?.authoring_mode || 'no_code'}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={asset.runtime_spec?.executor || 'cpu'}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>v{asset.latest_version_number || 0}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="AI Manager — chat about this model">
                      <IconButton
                        color="secondary"
                        onClick={() => navigate(`/ai/ml/models/${asset.id}/manager`)}
                      >
                        <AutoAwesomeIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Internals — 3D model visualization, metrics, features">
                      <IconButton
                        sx={{ color: '#ffb020' }}
                        onClick={() => navigate(`/ai/ml/models/${asset.id}/3d`)}
                      >
                        <InsightsIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Inspect">
                      <IconButton
                        color="info"
                        onClick={() => setAssetToInspect(asset)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        color="primary"
                        onClick={() => openEditDialog(asset)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Clone">
                      <IconButton
                        color="default"
                        onClick={() => openCloneDialog(asset)}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      color="error"
                      onClick={() => setAssetToDelete(asset)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(assetsQuery.data?.results || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No model assets yet. Use the visible no-code or code model buttons to
                    create the first blueprint.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Model Asset</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>Dataset Version</TableCell>
                <TableCell>Metrics</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(versionsQuery.data?.results || []).map((version) => (
                <TableRow key={version.id} hover>
                  <TableCell>{version.model_asset_name}</TableCell>
                  <TableCell>v{version.version_number}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={version.stage}
                      color={modelStageColor(version.stage)}
                    />
                  </TableCell>
                  <TableCell>{version.dataset_version_name || 'Manual registration'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {formatMetricSummary(version.metrics).map((metric) => (
                        <Chip key={metric} size="small" label={metric} variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Open AI Manager — inspect, visualize, deploy">
                      <IconButton
                        color="secondary"
                        onClick={() => navigate(`/ai/ml/models/${version.model_asset}/manager`)}
                      >
                        <AutoAwesomeIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Internals — 3D model visualization, metrics, features">
                      <IconButton
                        sx={{ color: '#ffb020' }}
                        onClick={() => navigate(`/ai/ml/models/${version.model_asset}/3d`)}
                      >
                        <InsightsIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download artifact">
                      <IconButton
                        color="default"
                        onClick={() => handleDownloadArtifact(version.id)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View lineage">
                      <IconButton
                        color="info"
                        onClick={() => setLineageVersionId(version.id)}
                      >
                        <AccountTreeIcon />
                      </IconButton>
                    </Tooltip>
                    {version.stage !== 'PRODUCTION' && (
                      <IconButton
                        color="primary"
                        onClick={() => promoteMutation.mutate(version.id)}
                      >
                        <ArrowUpwardIcon />
                      </IconButton>
                    )}
                    {version.stage !== 'DRAFT' && (
                      <IconButton
                        color="warning"
                        onClick={() => demoteMutation.mutate(version.id)}
                      >
                        <ArrowDownwardIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {(versionsQuery.data?.results || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No model versions yet. Training jobs will register them automatically.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullScreen
        PaperProps={{ sx: { bgcolor: 'background.default' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            px: 3,
            py: 1.5,
          }}
        >
          <IconButton
            edge="start"
            onClick={() => { setCreateOpen(false); setEditingAssetId(null); }}
            aria-label="close"
          >
            <ArrowDownwardIcon sx={{ transform: 'rotate(90deg)' }} />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              {dialogMode === 'edit' ? 'Edit Model Asset' : 'Register Model Asset'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dialogMode === 'edit' ? assetForm.name : 'Define a new model blueprint'}
            </Typography>
          </Box>
          <Button onClick={() => { setCreateOpen(false); setEditingAssetId(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (editingAssetId) {
                updateAssetMutation.mutate({ id: editingAssetId, form: assetForm });
              } else {
                createAssetMutation.mutate(assetForm);
              }
            }}
            disabled={
              !assetForm.name ||
              createAssetMutation.isPending ||
              updateAssetMutation.isPending
            }
          >
            {(createAssetMutation.isPending || updateAssetMutation.isPending)
              ? (editingAssetId ? 'Saving...' : 'Registering...')
              : (editingAssetId ? 'Save Changes' : 'Register Asset')}
          </Button>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, md: 6 }, py: 3, maxWidth: 1200, mx: 'auto', width: '100%' }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              Define a model asset blueprint. Choose <strong>No-Code Builder</strong> for a
              guided configuration or <strong>Restricted Python</strong> to author a
              sandboxed training recipe.
            </Alert>

            <Typography variant="overline" color="text.secondary">Identity</Typography>
            <TextField
              label="Asset name"
              placeholder="e.g. forex-classifier-v2"
              helperText="Unique identifier for this model asset"
              value={assetForm.name}
              onChange={(event) =>
                setAssetForm((current) => ({ ...current, name: event.target.value }))
              }
              fullWidth
              required
            />
            <TextField
              label="Description"
              placeholder="Brief description of what this model does"
              helperText="Visible in the registry table and inspect dialog"
              value={assetForm.description}
              onChange={(event) =>
                setAssetForm((current) => ({ ...current, description: event.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />
            <Tabs
              value={assetForm.authoringMode}
              onChange={(_, value) =>
                setAssetForm((current) => ({
                  ...current,
                  authoringMode: value,
                  framework:
                    value === 'python_sandbox' && current.framework === 'SKLEARN'
                      ? 'CUSTOM'
                      : current.framework,
                }))
              }
              variant="fullWidth"
            >
              <Tab value="no_code" label="No-Code Builder" />
              <Tab value="python_sandbox" label="Restricted Python" />
            </Tabs>
            <Divider />
            <Typography variant="overline" color="text.secondary">Classification & Runtime</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select
                label="Family"
                helperText="Model learning paradigm"
                value={assetForm.family}
                onChange={(event) =>
                  setAssetForm((current) => ({
                    ...current,
                    family: event.target.value as ModelLabFamily,
                  }))
                }
                fullWidth
              >
                <MenuItem value="ML">
                  <Box>
                    <Typography variant="body2">ML</Typography>
                    <Typography variant="caption" color="text.secondary">Classical machine learning</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="DL">
                  <Box>
                    <Typography variant="body2">DL</Typography>
                    <Typography variant="caption" color="text.secondary">Deep learning / neural nets</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="RL">
                  <Box>
                    <Typography variant="body2">RL</Typography>
                    <Typography variant="caption" color="text.secondary">Reinforcement learning</Typography>
                  </Box>
                </MenuItem>
              </TextField>
              <TextField
                select
                label="Framework"
                helperText="Training library"
                value={assetForm.framework}
                onChange={(event) =>
                  setAssetForm((current) => ({
                    ...current,
                    framework: event.target.value as ModelLabFramework,
                  }))
                }
                fullWidth
              >
                <MenuItem value="SKLEARN">scikit-learn</MenuItem>
                <MenuItem value="PYTORCH">PyTorch</MenuItem>
                <MenuItem value="CUSTOM">Custom</MenuItem>
              </TextField>
              <TextField
                select
                label="Problem type"
                helperText="Task category"
                value={assetForm.problemType}
                onChange={(event) =>
                  setAssetForm((current) => ({
                    ...current,
                    problemType: event.target.value as ModelLabProblemType,
                  }))
                }
                fullWidth
              >
                <MenuItem value="CLASSIFICATION">Classification</MenuItem>
                <MenuItem value="REGRESSION">Regression</MenuItem>
                <MenuItem value="TIME_SERIES">Time Series</MenuItem>
                <MenuItem value="REINFORCEMENT">Reinforcement</MenuItem>
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select
                label="Executor"
                helperText="Compute target for training"
                value={assetForm.executor}
                onChange={(event) =>
                  setAssetForm((current) => ({
                    ...current,
                    executor: event.target.value,
                  }))
                }
                fullWidth
              >
                <MenuItem value="cpu">CPU — Standard compute</MenuItem>
                <MenuItem value="gpu">GPU — Accelerated compute</MenuItem>
                <MenuItem value="distributed">Distributed — Multi-node</MenuItem>
              </TextField>
              <TextField
                label="Tags"
                placeholder="e.g. forex, production, v2"
                helperText="Comma-separated labels for filtering"
                value={assetForm.tags}
                onChange={(event) =>
                  setAssetForm((current) => ({
                    ...current,
                    tags: event.target.value,
                  }))
                }
                fullWidth
              />
            </Stack>
            {assetForm.authoringMode === 'no_code' ? (
              <Stack spacing={2}>
                <Divider />
                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountTreeIcon fontSize="small" /> Architecture Canvas
                </Typography>
                <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                  Build your model pipeline visually. Add preprocessing steps, choose a model
                  architecture (single, voting ensemble, or stacking), and configure each layer.
                </Alert>

                {/* ── Canvas Container ── */}
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    bgcolor: 'background.default',
                    backgroundImage:
                      'radial-gradient(circle, rgba(128,128,128,0.12) 1px, transparent 1px)',
                    backgroundSize: '20px 20px',
                  }}
                >
                  {/* Palette — add preprocessing layers */}
                  <Stack direction="row" spacing={0.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ alignSelf: 'center', mr: 0.5 }}
                    >
                      + Layer:
                    </Typography>
                    {PREPROCESSING_CATALOG.filter((step) => step.family === 'all' || (assetForm.family === 'DL' ? step.family === 'dl' : step.family === 'ml')).map((step) => (
                      <Chip
                        key={step.value}
                        label={step.label}
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setArchitecture((a) => ({
                            ...a,
                            preprocessing: [
                              ...a.preprocessing,
                              {
                                id: nextBlockId(),
                                type: step.value,
                                label: step.label,
                                params: { ...step.params },
                              },
                            ],
                          }))
                        }
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      />
                    ))}
                  </Stack>

                  {/* INPUT block */}
                  <CanvasBlock
                    color={BLOCK_COLORS.input}
                    title="INPUT"
                    subtitle={`Feature matrix • ${assetForm.featureStrategy === 'manual_contract' ? 'Manual columns' : 'Auto-detect from dataset'}`}
                  />
                  <FlowConnector />

                  {/* Preprocessing layers */}
                  {architecture.preprocessing.map((step) => (
                    <React.Fragment key={step.id}>
                      <CanvasBlock
                        color={BLOCK_COLORS.preprocessing}
                        title={step.label.toUpperCase()}
                        subtitle={PREPROCESSING_CATALOG.find((c) => c.value === step.type)?.desc}
                        onRemove={() =>
                          setArchitecture((a) => ({
                            ...a,
                            preprocessing: a.preprocessing.filter((s) => s.id !== step.id),
                          }))
                        }
                      >
                        {Object.keys(step.params).length > 0 && (
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                            {Object.entries(step.params).map(([k, v]) => (
                              <TextField
                                key={k}
                                label={k}
                                value={v}
                                size="small"
                                sx={{ width: 110 }}
                                onChange={(e) =>
                                  setArchitecture((a) => ({
                                    ...a,
                                    preprocessing: a.preprocessing.map((s) =>
                                      s.id === step.id
                                        ? { ...s, params: { ...s.params, [k]: e.target.value } }
                                        : s
                                    ),
                                  }))
                                }
                              />
                            ))}
                          </Stack>
                        )}
                      </CanvasBlock>
                      <FlowConnector />
                    </React.Fragment>
                  ))}

                  {/* ── Model Architecture section ── */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      border: '2px solid',
                      borderColor: BLOCK_COLORS.ensemble,
                      borderRadius: 2,
                    }}
                  >
                    <Tabs
                      value={architecture.modelType}
                      onChange={(_, v) => setArchitecture((a) => ({ ...a, modelType: v }))}
                      sx={{ mb: 2, minHeight: 36 }}
                    >
                      <Tab value="single" label="Single Model" sx={{ minHeight: 36 }} />
                      <Tab value="voting" label="Voting Ensemble" sx={{ minHeight: 36 }} />
                      <Tab value="stacking" label="Stacking Ensemble" sx={{ minHeight: 36 }} />
                    </Tabs>

                    {/* Single Model */}
                    {architecture.modelType === 'single' && (
                      <CanvasBlock color={BLOCK_COLORS.estimator} title="ESTIMATOR">
                        <EstimatorConfig
                          algorithm={architecture.singleEstimator.algorithm}
                          params={architecture.singleEstimator.params}
                          problemType={assetForm.problemType}
                          family={assetForm.family}
                          onAlgorithmChange={(alg) => {
                            const def = CANVAS_ALGORITHMS.find((a) => a.value === alg)?.defaults || {};
                            setArchitecture((a) => ({
                              ...a,
                              singleEstimator: { algorithm: alg, params: { ...def } },
                            }));
                            const ff = deriveFamilyFramework(alg);
                            setAssetForm((c) => ({ ...c, family: ff.family, framework: ff.framework }));
                          }}
                          onParamChange={(k, v) =>
                            setArchitecture((a) => ({
                              ...a,
                              singleEstimator: {
                                ...a.singleEstimator,
                                params: { ...a.singleEstimator.params, [k]: v },
                              },
                            }))
                          }
                        />
                      </CanvasBlock>
                    )}

                    {/* Voting Ensemble */}
                    {architecture.modelType === 'voting' && (
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <TextField
                            select
                            label="Voting Strategy"
                            value={architecture.votingStrategy}
                            onChange={(e) =>
                              setArchitecture((a) => ({
                                ...a,
                                votingStrategy: e.target.value as 'hard' | 'soft',
                              }))
                            }
                            size="small"
                            sx={{ width: 160 }}
                          >
                            <MenuItem value="hard">Hard Voting</MenuItem>
                            <MenuItem value="soft">Soft Voting</MenuItem>
                          </TextField>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setArchitecture((a) => ({
                                ...a,
                                baseEstimators: [
                                  ...a.baseEstimators,
                                  {
                                    id: nextBlockId(),
                                    algorithm: 'random_forest',
                                    params: { n_estimators: '100' },
                                  },
                                ],
                              }))
                            }
                          >
                            + Add Estimator
                          </Button>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          sx={{ overflowX: 'auto', pb: 1 }}
                        >
                          {architecture.baseEstimators.map((est, idx) => (
                            <Paper
                              key={est.id}
                              variant="outlined"
                              sx={{
                                borderLeft: `3px solid ${BLOCK_COLORS.estimator}`,
                                p: 1.5,
                                minWidth: 200,
                                flex: '0 0 auto',
                              }}
                            >
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mb: 0.5 }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: BLOCK_COLORS.estimator, fontWeight: 700 }}
                                >
                                  BASE #{idx + 1}
                                </Typography>
                                {architecture.baseEstimators.length > 2 && (
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      setArchitecture((a) => ({
                                        ...a,
                                        baseEstimators: a.baseEstimators.filter(
                                          (e) => e.id !== est.id
                                        ),
                                      }))
                                    }
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Stack>
                              <EstimatorConfig
                                algorithm={est.algorithm}
                                params={est.params}
                                problemType={assetForm.problemType}
                                family={assetForm.family}
                                compact
                                onAlgorithmChange={(alg) => {
                                  const def =
                                    CANVAS_ALGORITHMS.find((a) => a.value === alg)?.defaults || {};
                                  setArchitecture((a) => ({
                                    ...a,
                                    baseEstimators: a.baseEstimators.map((e) =>
                                      e.id === est.id
                                        ? { ...e, algorithm: alg, params: { ...def } }
                                        : e
                                    ),
                                  }));
                                  const ff = deriveFamilyFramework(alg);
                                  setAssetForm((c) => ({ ...c, family: ff.family, framework: ff.framework }));
                                }}
                                onParamChange={(k, v) =>
                                  setArchitecture((a) => ({
                                    ...a,
                                    baseEstimators: a.baseEstimators.map((e) =>
                                      e.id === est.id
                                        ? { ...e, params: { ...e.params, [k]: v } }
                                        : e
                                    ),
                                  }))
                                }
                              />
                            </Paper>
                          ))}
                        </Stack>
                        <FlowConnector />
                        <CanvasBlock
                          color={BLOCK_COLORS.ensemble}
                          title="VOTE COMBINER"
                          subtitle={`${architecture.votingStrategy} voting — ${architecture.baseEstimators.length} estimators`}
                        />
                      </Stack>
                    )}

                    {/* Stacking Ensemble */}
                    {architecture.modelType === 'stacking' && (
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            Base Estimators
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setArchitecture((a) => ({
                                ...a,
                                baseEstimators: [
                                  ...a.baseEstimators,
                                  {
                                    id: nextBlockId(),
                                    algorithm: 'random_forest',
                                    params: { n_estimators: '100' },
                                  },
                                ],
                              }))
                            }
                          >
                            + Add Base
                          </Button>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          sx={{ overflowX: 'auto', pb: 1 }}
                        >
                          {architecture.baseEstimators.map((est, idx) => (
                            <Paper
                              key={est.id}
                              variant="outlined"
                              sx={{
                                borderLeft: `3px solid ${BLOCK_COLORS.estimator}`,
                                p: 1.5,
                                minWidth: 200,
                                flex: '0 0 auto',
                              }}
                            >
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mb: 0.5 }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: BLOCK_COLORS.estimator, fontWeight: 700 }}
                                >
                                  BASE #{idx + 1}
                                </Typography>
                                {architecture.baseEstimators.length > 2 && (
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      setArchitecture((a) => ({
                                        ...a,
                                        baseEstimators: a.baseEstimators.filter(
                                          (e) => e.id !== est.id
                                        ),
                                      }))
                                    }
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Stack>
                              <EstimatorConfig
                                algorithm={est.algorithm}
                                params={est.params}
                                problemType={assetForm.problemType}
                                family={assetForm.family}
                                compact
                                onAlgorithmChange={(alg) => {
                                  const def =
                                    CANVAS_ALGORITHMS.find((a) => a.value === alg)?.defaults || {};
                                  setArchitecture((a) => ({
                                    ...a,
                                    baseEstimators: a.baseEstimators.map((e) =>
                                      e.id === est.id
                                        ? { ...e, algorithm: alg, params: { ...def } }
                                        : e
                                    ),
                                  }));
                                  const ff = deriveFamilyFramework(alg);
                                  setAssetForm((c) => ({ ...c, family: ff.family, framework: ff.framework }));
                                }}
                                onParamChange={(k, v) =>
                                  setArchitecture((a) => ({
                                    ...a,
                                    baseEstimators: a.baseEstimators.map((e) =>
                                      e.id === est.id
                                        ? { ...e, params: { ...e.params, [k]: v } }
                                        : e
                                    ),
                                  }))
                                }
                              />
                            </Paper>
                          ))}
                        </Stack>
                        <FlowConnector />
                        <CanvasBlock color={BLOCK_COLORS.meta_learner} title="META-LEARNER">
                          <EstimatorConfig
                            algorithm={architecture.metaLearner.algorithm}
                            params={architecture.metaLearner.params}
                            problemType={assetForm.problemType}
                            family={assetForm.family}
                            onAlgorithmChange={(alg) => {
                              const def =
                                CANVAS_ALGORITHMS.find((a) => a.value === alg)?.defaults || {};
                              setArchitecture((a) => ({
                                ...a,
                                metaLearner: { algorithm: alg, params: { ...def } },
                              }));
                              const ff = deriveFamilyFramework(alg);
                              setAssetForm((c) => ({ ...c, family: ff.family, framework: ff.framework }));
                            }}
                            onParamChange={(k, v) =>
                              setArchitecture((a) => ({
                                ...a,
                                metaLearner: {
                                  ...a.metaLearner,
                                  params: { ...a.metaLearner.params, [k]: v },
                                },
                              }))
                            }
                          />
                        </CanvasBlock>
                      </Stack>
                    )}
                  </Paper>

                  <FlowConnector />

                  {/* OUTPUT block */}
                  <CanvasBlock
                    color={BLOCK_COLORS.output}
                    title="OUTPUT"
                    subtitle={`${assetForm.problemType} prediction`}
                  />
                </Box>

                <TextField
                  label="Builder goal"
                  placeholder="Describe what the model should learn or predict"
                  helperText="Natural language description of the model's objective"
                  value={assetForm.builderGoal}
                  onChange={(event) =>
                    setAssetForm((current) => ({
                      ...current,
                      builderGoal: event.target.value,
                    }))
                  }
                  fullWidth
                  multiline
                  minRows={2}
                />
                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" color="text.secondary">Data Contract</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Target column"
                    placeholder="e.g. target, label, close_price"
                    helperText="Column the model predicts"
                    value={assetForm.targetName}
                    onChange={(event) =>
                      setAssetForm((current) => ({
                        ...current,
                        targetName: event.target.value,
                      }))
                    }
                    fullWidth
                  />
                  <TextField
                    label="Feature columns"
                    placeholder="e.g. open, high, low, volume"
                    helperText="Comma-separated — leave empty to auto-detect from dataset"
                    value={assetForm.featureColumns}
                    onChange={(event) =>
                      setAssetForm((current) => ({
                        ...current,
                        featureColumns: event.target.value,
                      }))
                    }
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    select
                    label="Objective metric"
                    helperText="Primary metric to optimize during training"
                    value={assetForm.objectiveMetric}
                    onChange={(event) =>
                      setAssetForm((current) => ({
                        ...current,
                        objectiveMetric: event.target.value,
                      }))
                    }
                    fullWidth
                  >
                    <MenuItem value="accuracy">Accuracy</MenuItem>
                    <MenuItem value="f1_weighted">F1 weighted</MenuItem>
                    <MenuItem value="mse">MSE</MenuItem>
                    <MenuItem value="mae">MAE</MenuItem>
                    <MenuItem value="r2">R2</MenuItem>
                    <MenuItem value="episode_reward">Episode reward</MenuItem>
                  </TextField>
                  <TextField
                    select
                    label="Validation strategy"
                    helperText="How training/test splits are performed"
                    value={assetForm.validationStrategy}
                    onChange={(event) =>
                      setAssetForm((current) => ({
                        ...current,
                        validationStrategy: event.target.value,
                      }))
                    }
                    fullWidth
                  >
                    <MenuItem value="holdout">Holdout split</MenuItem>
                    <MenuItem value="k_fold">K-fold</MenuItem>
                    <MenuItem value="rolling_window">Rolling window</MenuItem>
                    <MenuItem value="walk_forward">Walk-forward</MenuItem>
                    <MenuItem value="episode_eval">Episode evaluation</MenuItem>
                  </TextField>
                </Stack>
                <TextField
                  label="Evaluation metrics"
                  placeholder="accuracy, f1_weighted, recall_weighted"
                  helperText="Comma-separated secondary metrics tracked alongside the objective"
                  value={assetForm.evaluationMetrics}
                  onChange={(event) =>
                    setAssetForm((current) => ({
                      ...current,
                      evaluationMetrics: event.target.value,
                    }))
                  }
                  fullWidth
                />
                <Divider />
                <Typography variant="overline" color="text.secondary">Output & Notes</Typography>
                <TextField
                  label="Output schema JSON"
                  helperText='Map of prediction field names to types, e.g. {"prediction": "number", "confidence": "number"}'
                  value={assetForm.outputSchemaJson}
                  onChange={(event) =>
                    setAssetForm((current) => ({
                      ...current,
                      outputSchemaJson: event.target.value,
                    }))
                  }
                  fullWidth
                  multiline
                  minRows={4}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                />
                <TextField
                  label="Builder notes"
                  placeholder="Any additional context, constraints, or instructions for this model blueprint"
                  helperText="Stored alongside the asset for future reference"
                  value={assetForm.authoringNotes}
                  onChange={(event) =>
                    setAssetForm((current) => ({
                      ...current,
                      authoringNotes: event.target.value,
                    }))
                  }
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Divider />
                <Typography variant="subtitle1">Restricted Python Recipe</Typography>
                <Tabs
                  value={codeTab}
                  onChange={(_, v) => setCodeTab(v)}
                  sx={{ mb: 1 }}
                >
                  <Tab value="template" label="Templates" />
                  <Tab value="editor" label="Code Editor" />
                  <Tab value="docs" label={<Stack direction="row" spacing={0.5} alignItems="center"><MenuBookIcon fontSize="small" /><span>Documentation</span></Stack>} />
                </Tabs>

                {codeTab === 'template' && (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Pick a template to pre-fill the recipe, entrypoint, libraries, and classification settings.
                      You can customise the code after applying.
                    </Alert>
                    {TEMPLATE_FAMILIES.map((fam) => (
                      <Accordion key={fam.family} defaultExpanded={fam.family === 'ML'} disableGutters variant="outlined" sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle2">{fam.label}</Typography>
                          <Chip label={fam.categories.reduce((n, c) => n + c.templates.length, 0)} size="small" sx={{ ml: 1 }} />
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                          {fam.categories.map((cat) => (
                            <Box key={cat.label} sx={{ mb: 2 }}>
                              <Typography variant="overline" color="text.secondary">{cat.label}</Typography>
                              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                                {cat.templates.map((t) => (
                                  <Card key={t.id} variant="outlined" sx={{ width: 220, transition: '0.15s', '&:hover': { borderColor: 'primary.main', boxShadow: 2 } }}>
                                    <CardActionArea onClick={() => applyTemplate(t)} sx={{ p: 1.5 }}>
                                      <CardContent sx={{ p: '0 !important' }}>
                                        <Typography variant="body2" fontWeight={600}>{t.label}</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.3 }}>
                                          {t.description}
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap">
                                          <Chip label={t.framework} size="small" variant="outlined" />
                                          <Chip label={t.problemType} size="small" variant="outlined" />
                                        </Stack>
                                      </CardContent>
                                    </CardActionArea>
                                  </Card>
                                ))}
                              </Stack>
                            </Box>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                )}

                {codeTab === 'editor' && (
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        label="Entrypoint function"
                        placeholder="train_model"
                        helperText="The top-level function the executor calls to start training"
                        value={assetForm.entrypoint}
                        onChange={(event) =>
                          setAssetForm((current) => ({
                            ...current,
                            entrypoint: event.target.value,
                          }))
                        }
                        fullWidth
                      />
                      <TextField
                        label="Allowed libraries"
                        placeholder="json, math, numpy, pandas"
                        helperText="Comma-separated sandbox allowlist — only these imports are permitted"
                        value={assetForm.allowedLibraries}
                        onChange={(event) =>
                          setAssetForm((current) => ({
                            ...current,
                            allowedLibraries: event.target.value,
                          }))
                        }
                        fullWidth
                      />
                    </Stack>
                    <PythonCodeEditor
                      value={assetForm.recipeCode}
                      onChange={(value) =>
                        setAssetForm((current) => ({
                          ...current,
                          recipeCode: value,
                        }))
                      }
                      minHeight={360}
                      maxHeight={560}
                      availableInputs={[
                        { name: 'dataset_records', description: 'Raw dataset rows for training' },
                        { name: 'training_config', description: 'Selected training configuration' },
                        { name: 'runtime_spec', description: 'Saved runtime and resource settings' },
                        { name: 'metadata', description: 'Additional asset metadata' },
                      ]}
                      placeholder={defaultRecipeCode}
                    />
                  </Stack>
                )}

                {codeTab === 'docs' && (
                  <Paper variant="outlined" sx={{ p: 3, maxHeight: 520, overflow: 'auto' }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{RECIPE_DOCS}</ReactMarkdown>
                  </Paper>
                )}
              </Stack>
            )}
            <Divider />
            <Stack direction="row" alignItems="center" spacing={1}>
              <SettingsIcon fontSize="small" color="action" />
              <Typography variant="overline" color="text.secondary">Advanced Configuration</Typography>
            </Stack>
            <TextField
              label="Runtime overrides JSON"
              placeholder='{"timeout": 3600, "max_retries": 2}'
              helperText="Optional JSON merged into the runtime spec — use for custom executor settings, resource limits, or feature flags"
              value={assetForm.runtimeOverridesJson}
              onChange={(event) =>
                setAssetForm((current) => ({
                  ...current,
                  runtimeOverridesJson: event.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={4}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
            />
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(assetToInspect)}
        onClose={() => setAssetToInspect(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {assetToInspect?.name} Authoring Definition
        </DialogTitle>
        <DialogContent>
          {assetToInspect && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip label={assetToInspect.family} size="small" />
                <Chip label={assetToInspect.framework} size="small" />
                <Chip label={assetToInspect.problem_type} size="small" />
                <Chip
                  label={getAuthoringModeLabel(assetToInspect.runtime_spec?.authoring_mode)}
                  size="small"
                  color={
                    assetToInspect.runtime_spec?.authoring_mode === 'python_sandbox'
                      ? 'secondary'
                      : 'primary'
                  }
                />
              </Stack>
              {assetToInspect.runtime_spec?.authoring_mode === 'python_sandbox' ? (
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Sandbox contract
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Entrypoint:{' '}
                      {assetToInspect.runtime_spec?.authoring?.python?.entrypoint || 'train_model'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Allowlisted libraries:{' '}
                      {(assetToInspect.runtime_spec?.authoring?.python?.allowed_libraries || []).join(', ') ||
                        'default allowlist'}
                    </Typography>
                  </Paper>
                  <PythonCodeEditor
                    value={assetToInspect.runtime_spec?.authoring?.python?.code || ''}
                    onChange={() => {}}
                    disabled
                    minHeight={320}
                    maxHeight={520}
                    availableInputs={[
                      { name: 'dataset_records', description: 'Raw dataset rows for training' },
                      { name: 'training_config', description: 'Selected training configuration' },
                      { name: 'runtime_spec', description: 'Saved runtime and resource settings' },
                      { name: 'metadata', description: 'Additional asset metadata' },
                    ]}
                  />
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Architecture Blueprint
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Goal:{' '}
                      {assetToInspect.runtime_spec?.authoring?.no_code?.builder_goal || 'Not set'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Objective metric:{' '}
                      {assetToInspect.runtime_spec?.authoring?.no_code?.objective_metric || 'accuracy'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Validation:{' '}
                      {assetToInspect.runtime_spec?.authoring?.no_code?.validation_strategy || 'holdout'}
                    </Typography>
                  </Paper>
                  {/* Visual architecture summary */}
                  {(() => {
                    const arch = assetToInspect.runtime_spec?.authoring?.no_code?.architecture;
                    if (!arch) return <Alert severity="info" variant="outlined">No architecture canvas data saved (legacy asset).</Alert>;
                    return (
                      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: 'background.default', backgroundImage: 'radial-gradient(circle, rgba(128,128,128,0.12) 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
                        <CanvasBlock color={BLOCK_COLORS.input} title="INPUT" subtitle="Feature matrix" />
                        <FlowConnector />
                        {(arch.preprocessing || []).map((step: any, i: number) => (
                          <React.Fragment key={i}>
                            <CanvasBlock color={BLOCK_COLORS.preprocessing} title={(step.label || step.type || '').toUpperCase()} subtitle={Object.entries(step.params || {}).map(([k, v]) => `${k}=${v}`).join(', ') || undefined} />
                            <FlowConnector />
                          </React.Fragment>
                        ))}
                        {arch.model_type === 'single' && (
                          <CanvasBlock color={BLOCK_COLORS.estimator} title="ESTIMATOR" subtitle={`${arch.single_estimator?.algorithm || '?'} — ${Object.entries(arch.single_estimator?.params || {}).map(([k, v]) => `${k}=${v}`).join(', ')}`} />
                        )}
                        {arch.model_type === 'voting' && (
                          <Paper variant="outlined" sx={{ p: 1.5, border: `2px solid ${BLOCK_COLORS.ensemble}`, borderRadius: 2 }}>
                            <Typography variant="caption" sx={{ color: BLOCK_COLORS.ensemble, fontWeight: 700 }}>VOTING ENSEMBLE ({arch.voting_strategy})</Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1, overflowX: 'auto' }}>
                              {(arch.base_estimators || []).map((e: any, i: number) => (
                                <Chip key={i} label={e.algorithm} size="small" variant="outlined" sx={{ borderColor: BLOCK_COLORS.estimator }} />
                              ))}
                            </Stack>
                          </Paper>
                        )}
                        {arch.model_type === 'stacking' && (
                          <Paper variant="outlined" sx={{ p: 1.5, border: `2px solid ${BLOCK_COLORS.ensemble}`, borderRadius: 2 }}>
                            <Typography variant="caption" sx={{ color: BLOCK_COLORS.ensemble, fontWeight: 700 }}>STACKING ENSEMBLE</Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1, overflowX: 'auto' }}>
                              {(arch.base_estimators || []).map((e: any, i: number) => (
                                <Chip key={i} label={e.algorithm} size="small" variant="outlined" sx={{ borderColor: BLOCK_COLORS.estimator }} />
                              ))}
                            </Stack>
                            <FlowConnector />
                            <CanvasBlock color={BLOCK_COLORS.meta_learner} title="META-LEARNER" subtitle={arch.meta_learner?.algorithm} />
                          </Paper>
                        )}
                        <FlowConnector />
                        <CanvasBlock color={BLOCK_COLORS.output} title="OUTPUT" subtitle={`${assetToInspect.problem_type} prediction`} />
                      </Box>
                    );
                  })()}
                  <TextField
                    label="Feature columns"
                    value={
                      (assetToInspect.runtime_spec?.authoring?.no_code?.feature_columns || []).join(', ')
                    }
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Evaluation metrics"
                    value={
                      (assetToInspect.runtime_spec?.authoring?.no_code?.evaluation_metrics || []).join(', ')
                    }
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Output schema JSON"
                    value={JSON.stringify(
                      assetToInspect.runtime_spec?.authoring?.no_code?.output_schema || {},
                      null,
                      2
                    )}
                    multiline
                    minRows={6}
                    fullWidth
                    InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
                  />
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssetToInspect(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Lineage dialog */}
      <Dialog
        open={lineageVersionId != null}
        onClose={() => setLineageVersionId(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Model Lineage — Version #{lineageVersionId}</DialogTitle>
        <DialogContent>
          {lineageQuery.isLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {lineageQuery.data && (
            <LineageGraph data={lineageQuery.data as ModelLineage} />
          )}
          {lineageQuery.error && (
            <Alert severity="error">Failed to load lineage data.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLineageVersionId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(assetToDelete)}
        title="Delete model asset"
        message={`Delete ${assetToDelete?.name}? All linked versions and jobs will be impacted.`}
        confirmText="Delete"
        confirmColor="error"
        onCancel={() => setAssetToDelete(null)}
        onConfirm={() => {
          if (assetToDelete) {
            deleteAssetMutation.mutate(assetToDelete.id);
          }
        }}
      />
    </Box>
  );
};

export default ModelLabRegistryPage;